// ── Shared design tokens & types ──────────────────────────────────────────────

/**
 * Prefixes a relative path with Vite's runtime BASE_URL.
 *
 * The app is built with `base: 'v2'` in vite.config, so it's served from
 * `/v2/...` in production. Bundler-tracked imports are rewritten automatically,
 * but runtime `fetch("/geojson/...")` calls are NOT — they hit the SPA fallback
 * and return index.html as HTML, which then fails JSON.parse.
 *
 * Use this for any fetch of a file from /public/.
 *
 *   withBase("/geojson/poland-regions.geojson")
 *     → "/v2/geojson/poland-regions.geojson"   (prod)
 *     → "/geojson/poland-regions.geojson"      (dev, when BASE_URL is "/")
 *
 * Already-absolute URLs (http://...) and BASE-prefixed strings pass through
 * unchanged so it's safe to call on any path.
 */
export function withBase(path: string): string {
  if (/^https?:\/\//.test(path)) return path; // external URL — leave alone
  const base = import.meta.env.BASE_URL || '/'; // e.g. "/v2/" or "/"
  const baseTrim = base.endsWith('/') ? base.slice(0, -1) : base;
  const pathTrim = path.startsWith('/') ? path : `/${path}`;
  // Avoid double-prefix if path already starts with base
  if (baseTrim && pathTrim.startsWith(`${baseTrim}/`)) return pathTrim;
  return `${baseTrim}${pathTrim}`;
}

export const C = {
  noData: '#DAE2E6',
  low: '#3AD1B6',
  lowMid: '#7EBA6D',
  mid: '#FF9500',
  midHigh: '#FF6A00',
  high: '#DF5A5A',
  primary: '#4BB4B3',
  bg: '#F6F8F9',
  surface: '#EEF3F5',
  border: '#C5D4DB',
  borderSm: '#DAE2E6',
  textPrimary: '#1F3C4E',
  textSecondary: '#5E7B8C',
  textMuted: '#849EAD',
} as const;

export interface Incident {
  country: string; // ISO 3166-1 alpha-2 (e.g. "EE", "ES")
  postcode: string | null; // e.g. "10001"
  count: number;
}

export interface ThresholdConfig {
  low: number;
  mid: number;
}

// MapSharedData.tsx
export interface PostcodeCount {
  code: string | null; // Some incidents have no postcode, represented as null
  count: number;
}

export interface CountryIncidents {
  total: number;
  postcodes: PostcodeCount[];
}

export type IncidentsData = Record<string, CountryIncidents>;

export function getColor(count: number, cfg: ThresholdConfig): string {
  if (count === 0) return C.noData;
  if (count <= cfg.low * 0.5) return C.low;
  if (count <= cfg.low) return C.lowMid;
  if (count <= cfg.mid * 0.75) return C.mid;
  if (count <= cfg.mid) return C.midHigh;
  return C.high;
}

export function getSeverity(count: number, cfg: ThresholdConfig): { label: string; color: string } {
  if (count === 0) return { label: 'No data', color: C.textMuted };
  if (count <= cfg.low) return { label: 'Low', color: C.lowMid };
  if (count <= cfg.mid) return { label: 'Medium', color: C.mid };
  return { label: 'High', color: C.high };
}

// ── Slider ────────────────────────────────────────────────────────────────────

export interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  color: string;
  onChange: (v: number) => void;
}

export function Slider({ label, value, min, max, color, onChange }: SliderProps) {
  const pct = Math.round(((value - min) / (max - min)) * 100);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex justify-between">
        <span className="text-xs text-v2blueGray-600">{label}</span>
        <span
          className="text-xs font-semibold font-mono px-2 py-0.5 rounded-md"
          style={{ color, background: `${color}18` }}
        >
          {value}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full appearance-none h-1 rounded-sm outline-none cursor-pointer"
        style={{ background: `linear-gradient(to right, ${color} ${pct}%, ${C.border} ${pct}%)` }}
      />
      <div className="flex justify-between">
        <span className="text-[10px] text-v2blueGray-500">{min}</span>
        <span className="text-[10px] text-v2blueGray-500">{max}</span>
      </div>
    </div>
  );
}

// ── Bounds helper for GeoJSON ─────────────────────────────────────────────────

export function computeBounds(
  geojson: GeoJSON.FeatureCollection,
): [[number, number], [number, number]] | null {
  let minLng = Infinity,
    minLat = Infinity,
    maxLng = -Infinity,
    maxLat = -Infinity;
  const visit = (coords: any) => {
    if (typeof coords[0] === 'number') {
      const [lng, lat] = coords;
      if (lng < minLng) minLng = lng;
      if (lat < minLat) minLat = lat;
      if (lng > maxLng) maxLng = lng;
      if (lat > maxLat) maxLat = lat;
    } else {
      for (const c of coords) visit(c);
    }
  };
  for (const f of geojson.features) {
    if (f.geometry && 'coordinates' in f.geometry) visit(f.geometry.coordinates);
  }
  if (!isFinite(minLng)) return null;
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}

// ── Point-in-polygon (ray casting) ────────────────────────────────────────────

export function pointInFeature(point: [number, number], feature: GeoJSON.Feature): boolean {
  const geom = feature.geometry;
  if (!geom) return false;

  const inRing = (ring: number[][]): boolean => {
    const [x, y] = point;
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];
      const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  };

  const inPoly = (poly: number[][][]): boolean => {
    if (!inRing(poly[0])) return false;
    for (let i = 1; i < poly.length; i++) {
      if (inRing(poly[i])) return false;
    }
    return true;
  };

  if (geom.type === 'Polygon') return inPoly(geom.coordinates as number[][][]);
  if (geom.type === 'MultiPolygon') return (geom.coordinates as number[][][][]).some(inPoly);
  return false;
}

// ── Country code → readable name (small built-in list) ────────────────────────

export const COUNTRY_NAMES: Record<string, string> = {
  EE: 'Estonia',
  LV: 'Latvia',
  LT: 'Lithuania',
  FI: 'Finland',
  SE: 'Sweden',
  NO: 'Norway',
  DK: 'Denmark',
  DE: 'Germany',
  PL: 'Poland',
  NL: 'Netherlands',
  BE: 'Belgium',
  FR: 'France',
  ES: 'Spain',
  PT: 'Portugal',
  IT: 'Italy',
  GB: 'United Kingdom',
  IE: 'Ireland',
  CZ: 'Czechia',
  AT: 'Austria',
  CH: 'Switzerland',
  MT: 'Malta',
  CY: 'Cyprus',
  GR: 'Greece',
  HR: 'Croatia',
  SI: 'Slovenia',
  SK: 'Slovakia',
  HU: 'Hungary',
  RO: 'Romania',
  BG: 'Bulgaria',
  RS: 'Serbia',
  BA: 'Bosnia and Herzegovina',
  ME: 'Montenegro',
  MK: 'North Macedonia',
  AL: 'Albania',
  XK: 'Kosovo',
  LU: 'Luxembourg',
  LI: 'Liechtenstein',
  IS: 'Iceland',
  MD: 'Moldova',
  AD: 'Andorra',
  MC: 'Monaco',
  SM: 'San Marino',
  VA: 'Vatican City',
  GI: 'Gibraltar',
  US: 'United States',
  CA: 'Canada',
  MX: 'Mexico',
  BR: 'Brazil',
  AR: 'Argentina',
  RU: 'Russia',
  UA: 'Ukraine',
  BY: 'Belarus',
  TR: 'Turkey',
  JP: 'Japan',
  KR: 'South Korea',
  CN: 'China',
  IN: 'India',
  AU: 'Australia',
  IL: 'Israel',
  CO: 'Colombia',
  AE: 'United Arab Emirates',
  UY: 'Uruguay',
  UZ: 'Uzbekistan',
  VE: 'Venezuela',
  VN: 'Vietnam',
  PR: 'Puerto Rico',
  DZ: 'Algeria',
  AO: 'Angola',
  ZM: 'Zambia',
  AM: 'Armenia',
  AZ: 'Azerbaijan',
  BH: 'Bahrain',
  BJ: 'Benin',
  BM: 'Bermuda',
  BT: 'Bhutan',
  PE: 'Peru',
  BO: 'Bolivia',
  BW: 'Botswana',
  CM: 'Cameroon',
  AF: 'Afghanistan',
  BD: 'Bangladesh',
  MA: 'Morocco',
  CL: 'Chile',
  PY: 'Paraguay',
  EC: 'Ecuador',
  GY: 'Guyana',
  SR: 'Suriname',
  CU: 'Cuba',
  CR: 'Costa Rica',
  PA: 'Panama',
  DO: 'Dominican Republic',
  JM: 'Jamaica',
  HT: 'Haiti',
  BS: 'Bahamas',
  TW: 'Taiwan',
  PH: 'Philippines',
  ID: 'Indonesia',
  TH: 'Thailand',
  MY: 'Malaysia',
  KZ: 'Kazakhstan',
  MN: 'Mongolia',
  GE: 'Georgia',
  TJ: 'Tajikistan',
  KG: 'Kyrgyzstan',
  PG: 'Papua New Guinea',
  NZ: 'New Zealand',
  LK: 'Sri Lanka',
  NP: 'Nepal',
  MM: 'Myanmar',
  LA: 'Laos',
  KH: 'Cambodia',
  PK: 'Pakistan',
  GL: 'Greenland',
  MG: 'Madagascar',
  TM: 'Turkmenistan',
  EG: 'Egypt',
  ZA: 'South Africa',
  GT: 'Guatemala',
  BZ: 'Belize',
  SV: 'El Salvador',
};

// ── Country code → approx geographic center [lng, lat] ────────────────────────
// Used to position the loading spinner over the country before its GeoJSON
// is fetched (since we don't yet have exact bounds).

export const COUNTRY_CENTERS: Record<string, [number, number]> = {
  EE: [25.5, 58.7],
  LV: [24.6, 56.8],
  LT: [23.9, 55.3],
  FI: [25.7, 64.0],
  SE: [16.3, 62.0],
  NO: [10.0, 64.5],
  DK: [10.0, 56.0],
  DE: [10.4, 51.2],
  PL: [19.4, 52.0],
  NL: [5.3, 52.1],
  BE: [4.5, 50.6],
  FR: [2.5, 46.5],
  ES: [-3.7, 40.3],
  PT: [-8.2, 39.5],
  IT: [12.5, 42.8],
  GB: [-2.0, 54.5],
  IE: [-8.0, 53.4],
  CZ: [15.5, 49.8],
  AT: [14.5, 47.6],
  CH: [8.2, 46.8],
  MT: [14.4, 35.9],
  CY: [33.4, 35.1],
  GR: [22.0, 39.0],
  HR: [15.5, 45.1],
  SI: [14.8, 46.1],
  SK: [19.7, 48.7],
  HU: [19.5, 47.2],
  RO: [25.0, 45.9],
  BG: [25.5, 42.7],
  RS: [21.0, 44.0],
  BA: [17.8, 44.0],
  ME: [19.3, 42.7],
  MK: [21.7, 41.6],
  AL: [20.0, 41.0],
  XK: [20.9, 42.6],
  LU: [6.1, 49.8],
  LI: [9.55, 47.16],
  IS: [-19.0, 64.9],
  MD: [28.4, 47.0],
  AD: [1.5, 42.5],
  MC: [7.42, 43.74],
  SM: [12.45, 43.94],
  VA: [12.45, 41.9],
  GI: [-5.35, 36.14],
  US: [-98.5, 39.8],
  CA: [-106, 60.0],
  MX: [-102, 23.6],
  BR: [-54.0, -10.0],
  AR: [-64.0, -38.0],
  RU: [100.0, 60.0],
  UA: [31.0, 49.0],
  BY: [27.7, 53.7],
  TR: [35.0, 39.0],
  JP: [138.0, 36.5],
  KR: [127.7, 36.0],
  CN: [104.0, 35.0],
  IN: [79.0, 22.0],
  AU: [134.0, -25.0],
  IL: [34.85, 31.5],
  CO: [-74.0, 4.0],
  AE: [55.3, 24.0],
  UY: [-56.0, -32.5],
  UZ: [64.5, 41.5],
  VE: [-66.6, 6.4],
  VN: [108.3, 14.0],
  PR: [-66.1, 18.2],
  DZ: [3.0, 28.0],
  AO: [13.2, -8.8],
  ZM: [27.8, -13.1],
  AM: [45.0, 40.0],
  AZ: [47.5, 40.5],
  BH: [50.5, 26.0],
  BJ: [2.3, 9.5],
  BM: [-64.75, 32.3],
  BT: [90.4, 27.5],
  PE: [-75.0, -10.0],
  BO: [-64, -17],
  BW: [24.0, -22.0],
  CM: [12.0, 6.0],
  AF: [65.0, 33.0],
  BD: [90.4, 23.7],
  MA: [-5.0, 32.0],
  CL: [-71.0, -30.0],
  PY: [-58.0, -23.0],
  EC: [-78.5, -1.5],
  GY: [-58.0, 5.0],
  SR: [-56.0, 4.0],
  CU: [-79.5, 21.5],
  CR: [-84.0, 10.0],
  PA: [-80.0, 8.5],
  DO: [-70.0, 19.0],
  JM: [-77.0, 18.0],
  HT: [-72.5, 19.0],
  BS: [-77.5, 24.0],
  TW: [121.0, 23.5],
  PH: [122.0, 13.0],
  ID: [120.0, -2.0],
  TH: [100.0, 15.0],
  MY: [114.0, 2.5],
  KZ: [71.4, 51.0],
  MN: [103.0, 46.0],
  GE: [43.5, 42.0],
  TJ: [71.3, 38.5],
  KG: [74.6, 41.5],
  PG: [143.0, -6.0],
  NZ: [174.0, -41.0],
  LK: [79.9, 7.9],
  NP: [84.0, 28.0],
  MM: [96.0, 21.5],
  LA: [102.0, 18.0],
  KH: [105.0, 13.0],
  PK: [69.0, 30.0],
  GL: [-42.6, 71.7],
  MG: [46.0, -20.0],
  TM: [59.0, 39.0],
  EG: [30.0, 26.0],
  ZA: [24.0, -29.0],
  GT: [-90.5, 15.5],
  BZ: [-88.5, 17.2],
  SV: [-88.9, 13.8],
};
